// PAYG withholding per payment, following the ATO statements of formulas:
// Schedule 1 (regular pay), Schedule 5 Method A (bonuses / back pay /
// commissions) and Schedule 8 (study and training support loan component).
//
// Inputs and outputs are integer CENTS; the ATO formulas themselves work in
// whole dollars (weekly x = whole dollars + 99c; results rounded to the
// nearest dollar, 50c up), which this module reproduces exactly.
//
// Not modelled: the Medicare levy ADJUSTMENT (a Medicare levy variation
// declaration for low-income families), seniors/working-holiday schedules.

import { isoDate } from '../fy.js';
import {
  ADDITIONAL_PAYMENT_CAP,
  BANDED_STSL_RATES,
  OFFSET_SHARE,
  SCALE_4,
  SCHEDULE_1,
  SCHEDULE_8,
  versionFor,
} from './withholding-tables.js';

export const PERIODS_PER_YEAR = {
  weekly: 52,
  fortnightly: 26,
  'half-monthly': 24,
  monthly: 12,
  quarterly: 4,
};
export const PAY_FREQUENCIES = Object.keys(PERIODS_PER_YEAR);

/** Remove float noise, then round half up to a whole number. */
function roundHalfUp(value) {
  return Math.floor(Number(value.toPrecision(12)) + 0.5);
}

/** Floor after removing float noise (e.g. 2.9999999999 → 3, not 2). */
function floorClean(value) {
  return Math.floor(Number(value.toPrecision(12)));
}

function assertFrequency(frequency) {
  if (!PERIODS_PER_YEAR[frequency]) throw new RangeError(`Unknown pay frequency: ${frequency}`);
}

/**
 * Whole-dollar weekly equivalent of a period's earnings (before adding 99c):
 * fortnightly ÷ 2; monthly × 3 ÷ 13 (add 1c first if it ends in 33c);
 * quarterly ÷ 13. Cents are ignored.
 */
export function weeklyEquivalentDollars(grossCents, frequency) {
  const c = Math.max(0, Math.round(grossCents));
  switch (frequency) {
    case 'weekly':
      return Math.floor(c / 100);
    case 'fortnightly':
      return Math.floor(c / 200);
    case 'monthly':
      return Math.floor(((c % 100 === 33 ? c + 1 : c) * 3) / 1300);
    case 'quarterly':
      return Math.floor(c / 1300);
    default:
      throw new RangeError(`No weekly equivalent for ${frequency}`);
  }
}

/** A rounded weekly amount (whole dollars) → the period amount (whole dollars). */
function weeklyToPeriod(weeklyDollars, frequency) {
  switch (frequency) {
    case 'weekly':
      return weeklyDollars;
    case 'fortnightly':
      return weeklyDollars * 2;
    case 'monthly':
      return roundHalfUp((weeklyDollars * 13) / 3);
    default: // quarterly
      return weeklyDollars * 13;
  }
}

function coefficientY(rows, x) {
  const row = rows.find(([limit]) => x < limit);
  return Math.max(0, roundHalfUp(row[1] * x - row[2]));
}

/**
 * The Schedule 1 scale for a payee:
 * 1 no tax-free threshold · 2 tax-free threshold · 3 foreign resident ·
 * 4 no TFN · 5 full Medicare exemption · 6 half Medicare exemption.
 */
export function scaleFor({
  tfn = true,
  foreignResident = false,
  taxFreeThreshold = true,
  medicareExemption = 'none',
} = {}) {
  if (!tfn) return 4;
  if (foreignResident) return 3;
  if (!taxFreeThreshold) return 1;
  if (medicareExemption === 'full') return 5;
  if (medicareExemption === 'half') return 6;
  return 2;
}

/** Schedule 8 weekly STSL component (whole dollars) for weekly earnings W. */
function stslWeekly(version, weeklyDollars, useTftTable) {
  const x = weeklyDollars + 0.99;
  if (version.marginal) {
    return coefficientY(useTftTable ? version.marginal.tft : version.marginal.noTft, x);
  }
  const bounds = useTftTable ? version.banded.tft : version.banded.noTft;
  let rate = 0;
  bounds.forEach((lower, i) => {
    if (weeklyDollars >= lower) rate = BANDED_STSL_RATES[i];
  });
  return roundHalfUp(rate * x);
}

/** Withholding for weekly/fortnightly/monthly/quarterly periods, in whole dollars. */
function baseWithholding(grossCents, frequency, date, profile) {
  const scale = scaleFor(profile);
  if (scale === 4) {
    const rate = profile.foreignResident ? SCALE_4.foreign : SCALE_4.resident;
    return { scale, tax: floorClean(Math.floor(grossCents / 100) * rate), stsl: 0 };
  }
  const weekly = weeklyEquivalentDollars(grossCents, frequency);
  const x = weekly + 0.99;
  const sch1 = versionFor(SCHEDULE_1, date);
  let tax = weeklyToPeriod(coefficientY(sch1.scales[scale], x), frequency);
  if ([2, 5, 6].includes(scale) && profile.offsetsClaimedCents > 0) {
    const offset = roundHalfUp((profile.offsetsClaimedCents / 100) * OFFSET_SHARE[frequency]);
    tax = Math.max(0, tax - offset);
  }
  let stsl = 0;
  if (profile.studyLoan) {
    const useTft = profile.taxFreeThreshold !== false || profile.foreignResident === true;
    stsl = weeklyToPeriod(stslWeekly(versionFor(SCHEDULE_8, date), weekly, useTft), frequency);
  }
  return { scale, tax, stsl };
}

/**
 * PAYG withholding on one regular payment.
 * @param {number} grossCents — the period's gross taxable earnings.
 * @param {object} opts
 * @param {string} opts.frequency — one of PAY_FREQUENCIES.
 * @param {string|Date} opts.date — the payment date (picks the schedule version).
 * @param {object} [opts.profile] — { tfn, foreignResident, taxFreeThreshold,
 *   medicareExemption: 'none'|'half'|'full', studyLoan, offsetsClaimedCents }.
 * @returns {{ scale, taxCents, stslCents, totalCents }}
 *
 * Half-monthly ("bi-monthly") pays use the monthly method on the month's
 * total (twice this payment) and halve the result, per Schedule 1.
 */
export function paygWithholding(grossCents, { frequency, date, profile = {} }) {
  assertFrequency(frequency);
  const day = isoDate(date);
  // Scale 4 is a flat rate on THIS payment's earnings, so it never converts.
  if (frequency === 'half-monthly' && scaleFor(profile) !== 4) {
    const m = baseWithholding(grossCents * 2, 'monthly', day, profile);
    return result(m.scale, m.tax * 50, m.stsl * 50);
  }
  const w = baseWithholding(grossCents, frequency, day, profile);
  return result(w.scale, w.tax * 100, w.stsl * 100);
}

function result(scale, taxCents, stslCents) {
  return { scale, taxCents, stslCents, totalCents: taxCents + stslCents };
}

/**
 * Schedule 5 Method A — withholding on an ADDITIONAL payment (bonus,
 * commission, back pay) paid with a regular pay:
 *  1–2. W1 = withholding on the normal gross (cents ignored);
 *  3–5. W2 = withholding on normal gross + (additional ÷ periods/year, cents ignored);
 *  6–7. (W2 − W1) × periods;
 *  8–9. capped at 47% of the additional payment (incl. the STSL component),
 *       cents ignored, never negative.
 * `periods` defaults to the frequency's periods per year (a commission for a
 * shorter defined period may use that period count instead).
 * @returns {{ normal, additionalCents, totalCents }} — `normal` is W1's breakdown.
 */
export function additionalPaymentWithholding(
  normalGrossCents,
  additionalCents,
  { frequency, date, profile = {}, periods = PERIODS_PER_YEAR[frequency] },
) {
  assertFrequency(frequency);
  const opts = { frequency, date, profile };
  const normal = paygWithholding(Math.floor(normalGrossCents / 100) * 100, opts);
  const spread = Math.floor(additionalCents / periods / 100) * 100;
  const withExtra = paygWithholding(Math.floor(normalGrossCents / 100) * 100 + spread, opts);
  const uncapped = (withExtra.totalCents - normal.totalCents) * periods;
  const cap = additionalCents * ADDITIONAL_PAYMENT_CAP;
  const additional = Math.max(0, floorClean(Math.min(uncapped, cap) / 100) * 100);
  return { normal, additionalCents: additional, totalCents: normal.totalCents + additional };
}

/**
 * Optional extra withholding an employee may request in a year with 53
 * weekly or 27 fortnightly pays (Schedule 1). Returns cents (0 otherwise).
 */
export function extraPayWithholding(grossCents, { frequency, date }) {
  const table = frequency === 'weekly' ? 'extra53' : frequency === 'fortnightly' ? 'extra27' : null;
  if (!table) return 0;
  const dollars = Math.floor(grossCents / 100);
  let amount = 0;
  for (const [from, extra] of versionFor(SCHEDULE_1, isoDate(date))[table]) {
    if (dollars >= from) amount = extra;
  }
  return amount * 100;
}
